<template>
  <v-container fluid>
    <div class="d-flex justify-space-between align-center">
      <h2>{{ $tc('customer', 2) }}</h2>
      <v-btn
        v-if="userLoaded && user.can('create_user')"
        outlined
        to="/customer/create"
        color="primary">
        <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
        {{ $t('new_customer') }}
      </v-btn>
    </div>
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="data-table-wrapper" :class="{'loading': loadingCustomers}">
        <v-data-table
          hide-default-footer
          disable-pagination
          class="vf-table mt-2"
          :headers="tableFields"
          :items="customerResults"
          :loading="loadingCustomers"
          :loading-text="$t('loading')"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :custom-sort="sortCustomers">
          <template #item.name="row">
            <span v-if="row.item.parent_id" class="ml-4 text-no-wrap">
              <router-link :to="/customer/ + row.item.id" text-no-wrap>
                <v-icon fixed-width>mdi-account</v-icon>
                {{ row.item.name }}
              </router-link>
            </span>
            <span v-else class="text-no-wrap">
              <router-link :to="/customer/ + row.item.id">
                <v-icon fixed-width>mdi-office-building</v-icon>
                {{ row.item.name }}
              </router-link>
            </span>
          </template>
          <template #item.status="row">
            <span v-if="row.item.getSetting('enterpriseOnboardingComplete')">
              <vf-tooltip text="Enterprise Onboarding Complete">
                <v-icon small class="mr-2" color="primary">
                  mdi-check-circle
                </v-icon>
              </vf-tooltip>
            </span>
            <span v-else-if="row.item.getSetting('onboardingComplete')">
              <vf-tooltip text="Baseline Onboarding Complete">
                <v-icon small class="mr-2" color="green">
                  mdi-check-circle
                </v-icon>
              </vf-tooltip>
            </span>
            <span v-else>
              <vf-tooltip text="Onboarding Incomplete">
                <v-icon small class="mr-2">
                  mdi-circle-outline
                </v-icon>
              </vf-tooltip>
            </span>
            <span v-if="get(row.item.getSetting('processing.RouteDetection'), 'enableRouteStarting')">
              <vf-tooltip :text="$t('automatic_route_creation_enabled')">
                <v-icon small color="primary">
                  mdi-car-wireless
                </v-icon>
              </vf-tooltip>
            </span>
          </template>
          <template #item.parent_id="row">
            <if-set :value="getCustomerById(row.item.parent_id)">
              <template slot-scope="scope">
                <router-link :to="/customer/ + scope.value.id">
                  {{ scope.value.name }}
                </router-link>
              </template>
            </if-set>
          </template>
          <template #item.timezone="row">
            <if-set :value="row.item.timezone()"></if-set>
          </template>
          <template #item.temperature="row">
            <if-set :value="row.item.tempFormat()"></if-set>
          </template>
          <template #item.customer_service_providers="row">
            <vf-tooltip
              v-if="size(row.value) > 0"
              :text="row.value.map(sp => sp.service_provider.name).join(', ')">
              <div class="text-truncate" style="width: 100px">
                <a href="#">{{ size(row.value) }}</a>
              </div>
            </vf-tooltip>
          </template>
          <template #item.customer_stats.count_routes="row">
            <v-skeleton-loader type="table-cell" v-if="loadingCustomerStats"/>
            <span v-else>{{ row.value }}</span>
          </template>
          <template #item.customer_stats.count_vehicles="row">
            <v-skeleton-loader type="table-cell" v-if="loadingCustomerStats"/>
            <span v-else>{{ row.value }}</span>
          </template>
          <template #item.customer_stats.count_locations="row">
            <v-skeleton-loader type="table-cell" v-if="loadingCustomerStats"/>
            <span v-else>{{ row.value }}</span>
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </v-container>
</template>

<script>
import VfTooltip from '@/components/shared/VfTooltip.vue'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import { each, find, keyBy, get, size } from 'lodash'

export default {
  name: 'CustomerIndex',
  components: {
    VfTooltip
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin
  ],
  data () {
    return {
      customerResults: [],
      loadingCustomers: true,
      loadingCustomerStats: true,
      sortBy: 'name',
      sortDesc: false,
      tableFields: [
        {
          value: 'name',
          text: this.$t('name'),
          sortable: true
        },
        {
          value: 'status',
          text: this.$t('status')
        },
        {
          value: 'parent_id',
          text: this.$t('parent'),
          sortable: true
        },
        {
          value: 'timezone',
          text: this.$t('timezone'),
          sortable: false
        },
        {
          value: 'temperature',
          text: this.$t('temperature'),
          sortable: false
        },
        {
          value: 'customer_service_providers',
          text: this.$t('telematics'),
          sortable: false
        },
        {
          value: 'customer_stats.count_routes',
          text: this.$t('number_of_routes'),
          sortable: false
        },
        {
          value: 'customer_stats.count_vehicles',
          text: this.$t('number_of_vehicles'),
          sortable: false
        },
        {
          value: 'customer_stats.count_locations',
          text: this.$t('number_of_locations'),
          sortable: false
        }
      ]
    }
  },

  methods: {
    // Lodash convenience accessors for template
    size: size,
    get: get,
    /**
     * Custom sorting function for the table - for 'name', will sort customers by name, hierarchically. Otherwise uses
     * default table sort. Note that this function duplicates the functionality of
     * CustomerRecord.sortCustomersHierarchically, but it is somewhat necessary to replicate it here because are
     * implementing a sort function on <b-table>'s terms. (This also means the slightly awkward step of injecting the
     * parent's name into each customer object via mapParentNames.)
     */
    sortCustomers (items, index, sortDesc) {
      items.sort((a, b) => {
        if (index === 'name') {
          // Both are top level
          if (a.parent_id === null && b.parent_id === null) {
            return a.name < b.name ? -1 : 1
          }
          // A is top level, B is not
          if (a.parent_id === null) {
            // A's parent is B, A should be sorted after
            if (a.name === b.parent_name) {
              // if descending, flip to preserve hierarchy
              return this.sortDesc ? 1 : -1
            }
            return a.name < b.parent_name ? -1 : 1
          }
          // B is top level, A is not
          if (b.parent_id === null) {
            // B's parent is A, A should be sorted before
            if (b.name === a.parent_name) {
              // if descending, flip to preserve hierarchy
              return this.sortDesc ? -1 : 1
            }
            return a.parent_name < b.name ? -1 : 1
          }
          // Parents are same
          if (a.parent_id === b.parent_id) {
            return a.name < b.name ? -1 : 1
          }
          // Parents not same
          return a.parent_name < b.parent_name ? -1 : 1
        } else {
          if (this.sortDesc === false) {
            return a[index] < b[index] ? -1 : 1
          } else {
            return b[index] < a[index] ? -1 : 1
          }
        }
      })
      return items
    },
    /**
     * Map parent names to customers if available. This is a convenience function
     * to prep customers for sorting in sortCustomers.
     */
    mapParentNames (customers) {
      each(customers, a => {
        if (a.parent_id !== null) {
          const match = find(customers, b => {
            return a.parent_id === b.id
          })
          if (match !== null) {
            a.parent_name = match.name
          }
        }
      })
      return customers
    },
    /**
     * Fetch the main customer data from the backend.
     * @returns {Promise<void>}
     */
    async fetchCustomers () {
      this.loadingCustomers = true
      const expand = [
        'customer_service_providers.service_provider',
        'customer_settings'
      ]
      try {
        const results = await this.$orm('Customer')
          .findAll({
            expand: expand.join(',')
          })
        this.customerResults = this.mapParentNames(results.getItems())
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loadingCustomers = false
      }
    },
    /**
     * Fetch the auxiliary customer stats from the backend. This is done separately as this part of the
     * request can take some time (although subsequent loads will be cached.)
     * @returns {Promise<void>}
     */
    async fetchCustomerStats () {
      this.loadingCustomerStats = true
      try {
        const results = await this.$orm('Customer')
          .findAll({
            fields: 'id',
            expand: 'customer_stats'
          })
        const mappedResults = keyBy(results.getItems(), 'id')
        each(this.customerResults, customer => {
          customer.customer_stats = get(mappedResults, `${customer.id}.customer_stats`)
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loadingCustomerStats = false
      }
    },
    getCustomerById (customerId) {
      return this.customerResults.find(customer => customer.id === customerId)
    }
  },
  mounted () {
    this.fetchCustomers().then(() => {
      this.fetchCustomerStats()
    })
  }
}
</script>
